/**
 * File responsible for mapping the redux state.
 */
import { RouterState } from 'redux-first-history';
import { combineReducers, Reducer } from '@reduxjs/toolkit';

// Re-export ApplicationState from storeTypes to maintain backward compatibility
export type { ApplicationState } from './storeTypes';

// Reducers
import authReducer from './auth/authSlice';
import storedRouteReducer from './storedRoute/storedRouteSlice';
import rolesReducer from './roles/roleSlice';
import smsProvidersReducer from './smsProviders/smsProvidersSlice';
import usersReducer from './users/userSlice';
import invitesReducer from './invites/inviteSlice';
import companiesReducer from './companies/companySlice';
import importReducer from './import/importSlice';
import invoicesReducer from './invoices/invoiceSlice';
import customersReducer from './customers/customerSlice';
import notificationsReducer from './notifications/notificationSlice';
import tasksReducer from './tasks/taskSlice';
import ticketsReducer from './tickets/ticketSlice';
import commonReducer from './common/commonSlice';
import accessTokensReducer from './accessTokens/accessTokenSlice';
import creditsReducer from './credits/creditSlice';
import dashboardsReducer from './dashboards/dashboardSlice';
import paymentsReducer from './payments/paymentSlice';
import organisationsReducer from './organisations/organisationSlice';
import paymentPlansReducer from './paymentPlans/paymentPlanSlice';
import remittanceAdvicesReducer from './remittanceAdvices/remittanceAdviceSlice';
import supportTicketsReducer from './supportTickets/supportTicketSlice';
import contentsReducer from './contents/contentSlice';
import changesReducer from './changes/changeSlice';
import workflowsReducer from './workflow/workflowSlice';
import reportReducer from './report/reportSlice';
import scheduledReportReducer from './scheduledReports/scheduledReportSlice';
import banksReducer from './banks/bankSlice';
import bankFileDescriptorsReducer from './bankFileDescriptors/bankFileDescriptorSlice';
import customerPortalReducer from './customerPortal/customerPortalSlice';
import salesOrdersReducer from './salesOrders/salesOrdersSlice';
import paymentBehaviourInsightsReducer from './paymentBehaviourInsights/paymentBehaviourInsightsSlice';
import webhookConfigurationReducer from './webhookConfiguration/webhookConfigurationSlice';

// Additional props for connected React components. This prop is passed by default with `connect()`
// export interface ConnectedReduxProps<A extends Action = AnyAction> {
//     dispatch: Dispatch<A>;
// }

// Whenever an action is dispatched, Redux will update each top-level application state property
// using the reducer with the matching name. It's important that the names match exactly, and that
// the reducer acts on the corresponding ApplicationState property type.
export const createRootReducer = (routerReducer: Reducer<RouterState>) =>
    combineReducers({
        app: commonReducer,
        auth: authReducer,
        storedRoute: storedRouteReducer,
        roles: rolesReducer,
        smsProviders: smsProvidersReducer,
        users: usersReducer,
        invites: invitesReducer,
        companies: companiesReducer,
        import: importReducer,
        invoices: invoicesReducer,
        customers: customersReducer,
        customerPortal: customerPortalReducer,
        notifications: notificationsReducer,
        tasks: tasksReducer,
        tickets: ticketsReducer,
        accessTokens: accessTokensReducer,
        credits: creditsReducer,
        dashboards: dashboardsReducer,
        payments: paymentsReducer,
        organisations: organisationsReducer,
        paymentPlans: paymentPlansReducer,
        remittanceAdvices: remittanceAdvicesReducer,
        changes: changesReducer,
        workflows: workflowsReducer,
        report: reportReducer,
        scheduledReport: scheduledReportReducer,
        contents: contentsReducer,
        supportTickets: supportTicketsReducer,
        banks: banksReducer,
        bankFileDescriptors: bankFileDescriptorsReducer,
        salesOrders: salesOrdersReducer,
        paymentBehaviourInsights: paymentBehaviourInsightsReducer,
        webhookConfiguration: webhookConfigurationReducer,
        router: routerReducer,
        //conversationLine: conversationLineReducer
    });

// rootSaga is now exported from rootSaga.ts to avoid circular dependencies
export { rootSaga } from './rootSaga';
